import React, { useState, useEffect, useCallback } from "react";
import { apiGetRequest, apiPostRequest, apiPutRequest } from "helpers/Requests";
import { URL_CUSTOM_LINE_ITEMS } from "store/services/URL";
import { URL_ROSTER_INVOICE_OTHER_LINES } from "store/services/URL";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import FundingTypeHelpIcon from "components/common/FundingTypeHelpIcon/FundingTypeHelpIcon";

const FUNDING_TYPES = [
  { value: "NDIS", label: "NDIS" },
  { value: "AGED_CARE", label: "Aged Care" },
  { value: "PRIVATE", label: "Private" },
  { value: "OTHER", label: "Other" },
];

// Australian states/territories for one-off NDIS lines (stored in location field)
const AU_STATES = [
  { value: "", label: "Select state..." },
  { value: "Australian Capital Territory", label: "Australian Capital Territory" },
  { value: "New South Wales", label: "New South Wales" },
  { value: "Northern Territory", label: "Northern Territory" },
  { value: "Queensland", label: "Queensland" },
  { value: "South Australia", label: "South Australia" },
  { value: "Tasmania", label: "Tasmania" },
  { value: "Victoria", label: "Victoria" },
  { value: "Western Australia", label: "Western Australia" },
];

const ONE_OFF_TEMPLATES = [
  { label: "Admin fee", description: "Admin fee", quantity: 1 },
  { label: "Food recovery", description: "Food recovery", quantity: 1 },
  { label: "Transport", description: "Transport", quantity: 1 },
  { label: "Consumables", description: "Consumables", quantity: 1 },
  { label: "Other", description: "Other", quantity: 1 },
];

function computeLineTotals(quantity, unitRate, gstApplicable) {
  const qty = parseFloat(quantity) || 0;
  const rate = parseFloat(unitRate) || 0;
  const subtotal = Math.round(qty * rate * 100) / 100;
  const gstRate = gstApplicable ? 0.1 : 0;
  const gst = Math.round(subtotal * gstRate * 100) / 100;
  const total = Math.round((subtotal + gst) * 100) / 100;
  return { subtotal, gst, total, gstApplicable: !!gstApplicable };
}

const OtherLinesModal = ({
  open,
  onClose,
  mode,
  invoiceUuid,
  line = null,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState("custom");
  const [customOptions, setCustomOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [customUuid, setCustomUuid] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitRate, setUnitRate] = useState("");
  const [rateOverridden, setRateOverridden] = useState(false);
  const [fundingType, setFundingType] = useState("NDIS");
  const [code, setCode] = useState("");
  const [serviceCategory, setServiceCategory] = useState("");
  const [location, setLocation] = useState("");

  const isEdit = mode === "edit" && line;
  const gstApplicable = fundingType === "PRIVATE" || fundingType === "OTHER";
  const lineTotals = computeLineTotals(quantity, unitRate, gstApplicable);

  const fetchCustomOptions = useCallback(() => {
    if (!open) return;
    setLoadingOptions(true);
    const params = new URLSearchParams({ status: "active", per_page: 200 });
    if (isEdit && line?.source_type === "CUSTOM_LIBRARY" && line?.source_id) {
      params.set("include_item_id", line.source_id);
    }
    apiGetRequest(`${URL_CUSTOM_LINE_ITEMS}/select-options?${params}`)
      .then((res) => setCustomOptions(res?.data?.data ?? []))
      .catch(() => setCustomOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [open, isEdit, line?.source_type, line?.source_id]);

  useEffect(() => {
    if (open) {
      setError(null);
      if (isEdit) {
        setActiveTab(line.source_type === "ONE_OFF" ? "oneoff" : "custom");
        setDescription(line.description || "");
        setQuantity(String(line.quantity ?? 1));
        setUnitRate(String(line.unit_rate ?? ""));
        setRateOverridden(!!line.rate_overridden);
        setFundingType(line.funding_type || "NDIS");
        setCode(line.code || "");
        setServiceCategory(line.service_category || "");
        setLocation(line.location || "");
        if (line.source_type === "CUSTOM_LIBRARY" && line.source_id) {
          setCustomUuid("");
        }
      } else {
        setActiveTab("custom");
        setCustomUuid("");
        setDescription("");
        setQuantity("1");
        setUnitRate("");
        setRateOverridden(false);
        setFundingType("NDIS");
        setCode("");
        setServiceCategory("");
        setLocation("");
      }
      fetchCustomOptions();
    }
  }, [open, isEdit, line, fetchCustomOptions]);

  useEffect(() => {
    if (open && isEdit && line?.source_type === "CUSTOM_LIBRARY" && line?.source_id && customOptions.length > 0) {
      const opt = customOptions.find((o) => o.id === line.source_id);
      if (opt?.uuid) setCustomUuid(opt.uuid);
    }
  }, [open, isEdit, line?.source_type, line?.source_id, customOptions]);

  useEffect(() => {
    if (activeTab === "custom" && customUuid) {
      const item = customOptions.find((o) => o.uuid === customUuid);
      if (item) {
        setDescription(item.description || "");
        setFundingType(item.funding_type || "NDIS");
        if (!rateOverridden) setUnitRate(String(item.rate ?? ""));
      }
    }
  }, [customUuid, customOptions, activeTab, rateOverridden]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const qty = parseFloat(quantity);
    const rate = parseFloat(unitRate);
    if (!qty || qty <= 0) {
      setError("Quantity must be greater than 0.");
      return;
    }
    if (rate < 0) {
      setError("Unit rate cannot be negative.");
      return;
    }
    if (!description || !description.trim()) {
      setError("Description is required.");
      return;
    }
    if (activeTab === "custom") {
      if (!customUuid) {
        setError("Please select a custom line item.");
        return;
      }
    } else {
      if (!fundingType) {
        setError("Funding type is required for one-off lines.");
        return;
      }
    }

    const baseUrl = URL_ROSTER_INVOICE_OTHER_LINES(invoiceUuid);
    setSubmitting(true);
    const payload = {
      source_type: activeTab === "custom" ? "CUSTOM_LIBRARY" : "ONE_OFF",
      description: description.trim(),
      quantity: qty,
      unit_rate: rate,
    };
    if (activeTab === "custom") {
      payload.source_id = customUuid;
      if (rateOverridden) payload.rate_overridden = true;
    } else {
      payload.funding_type = fundingType;
      if (code) payload.code = code.trim();
      if (serviceCategory) payload.service_category = serviceCategory.trim();
      if (location) payload.location = location.trim();
    }

    const request = isEdit
      ? apiPutRequest(`${baseUrl}/${line.uuid}`, payload)
      : apiPostRequest(baseUrl, payload);

    request
      .then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          toast.success(isEdit ? "Line updated." : "Line added.");
          onSuccess?.(res?.data);
          onClose();
        } else {
          const msg = res?.data?.message || "Request failed.";
          setError(msg);
          toast.error(msg);
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || "Request failed.";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => setSubmitting(false));
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto">
        <div className="fixed inset-0 bg-black opacity-25" onClick={onClose} aria-hidden="true" />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{isEdit ? "Edit other line" : "Add other line"}</h2>
          </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex border-b">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${activeTab === "custom" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
              onClick={() => setActiveTab("custom")}
            >
              From Custom Line Items
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${activeTab === "oneoff" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
              onClick={() => setActiveTab("oneoff")}
            >
              One-off line
            </button>
          </div>
          <div className="p-4 overflow-y-auto flex-1 space-y-3">
            {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

            {activeTab === "custom" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom line item</label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    value={customUuid}
                    onChange={(e) => setCustomUuid(e.target.value)}
                    disabled={loadingOptions}
                  >
                    <option value="">Select...</option>
                    {customOptions.map((opt) => (
                      <option key={opt.uuid} value={opt.uuid}>
                        {opt.unique_code} – {opt.description} (${parseFloat(opt.rate || 0).toFixed(2)}) {opt.status !== "active" ? "[Inactive]" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit rate ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={unitRate}
                      onChange={(e) => setUnitRate(e.target.value)}
                    />
                  </div>
                </div>
                {customUuid && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rateOverridden}
                      onChange={(e) => setRateOverridden(e.target.checked)}
                    />
                    <span className="text-sm">Override rate</span>
                  </label>
                )}
              </>
            )}

            {activeTab === "oneoff" && (
              <>
                {!isEdit && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quick templates</label>
                    <div className="flex flex-wrap gap-2">
                      {ONE_OFF_TEMPLATES.map((t) => (
                        <button
                          key={t.label}
                          type="button"
                          className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100"
                          onClick={() => {
                            setDescription(t.description);
                            setQuantity(String(t.quantity));
                          }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Funding type *
                    <FundingTypeHelpIcon />
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    value={fundingType}
                    onChange={(e) => setFundingType(e.target.value)}
                  >
                    {FUNDING_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code (optional)</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={50}
                    placeholder="e.g. ONE-OFF"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit rate ($) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={unitRate}
                      onChange={(e) => setUnitRate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service category</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      value={serviceCategory}
                      onChange={(e) => setServiceCategory(e.target.value)}
                    />
                  </div>
                  {fundingType === "NDIS" ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      >
                        {AU_STATES.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. NSW"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="pt-2 border-t text-sm text-gray-600">
              <span>GST: {lineTotals.gstApplicable ? "10%" : "GST-free"}</span>
              <span className="ml-4">Subtotal: ${lineTotals.subtotal.toFixed(2)}</span>
              {lineTotals.gstApplicable && <span className="ml-4">GST: ${lineTotals.gst.toFixed(2)}</span>}
              <span className="ml-4 font-medium">Total: ${lineTotals.total.toFixed(2)}</span>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
            <CustomButton type="button" variant="secondary" onClick={onClose} label="Cancel" />
            <CustomButton type="submit" variant="primary" showLoading={submitting} label={isEdit ? "Update" : "Add line"} />
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default OtherLinesModal;
